import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ReactComponent as Cross } from "../../Resources/image/cross.svg";
import AnimatedCart from '../../Containers/AnimatedPage/AnimatedCart';
import { post, getRequestErrorMessage } from '../../utils/request';
import { fetchIsSystemUser } from '../../utils/loginSystemUser';
import { useToast } from '../Toast/Toast';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { VK_CONFIG } from '../../utils/vkConfig';
import { beginVkPkceSession } from '../../utils/vkPkce';
import { t } from '../../utils/i18n';
import styles from './Login.module.css';

const Login = props => {
    const {
        handleCloseCart,
        cartDisplayed,
        handleHover,
        hoverState,
        onLoginSuccess
    } = props;

    const toast = useToast();
    const [loginMethod, setLoginMethod] = useState('phone');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [countdownByMethod, setCountdownByMethod] = useState({
        phone: 0,
        email: 0
    });
    const [systemUserByMethod, setSystemUserByMethod] = useState({
        phone: false,
        email: false
    });
    const [rememberMe, setRememberMe] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState('RU');
    const [isLoginLoading, setIsLoginLoading] = useState(false);
    const [isSendCodeLoading, setIsSendCodeLoading] = useState(false);
    /** 当前账号是否已成功请求过验证码（切换手机/邮箱/登录方式后需重新获取） */
    const [verificationCodeRequested, setVerificationCodeRequested] = useState(false);

    // 协议内容：用户协议 + 隐私协议（富文本，只读展示）
    const [agreements, setAgreements] = useState([]);
    const [agreementsLoading, setAgreementsLoading] = useState(false);
    const [activeAgreement, setActiveAgreement] = useState(null);
    const [showAgreementModal, setShowAgreementModal] = useState(false);
    const [registerConfirmState, setRegisterConfirmState] = useState(null);
    const registerConfirmResolverRef = useRef(null);
    const phoneSystemUserCheckTimerRef = useRef(null);
    const emailSystemUserCheckTimerRef = useRef(null);

    const countries = [
        { code: 'RU', name: 'Russia', dialCode: '+7', flag: '🇷🇺' }
    ];

    const currentCountdown = countdownByMethod[loginMethod] || 0;
    const isCurrentSystemUser = Boolean(systemUserByMethod[loginMethod]);

    const clearSendCodeCountdown = (method) => {
        setCountdownByMethod((prev) => ({
            ...prev,
            [method]: 0
        }));
        try {
            localStorage.removeItem(`login_last_send_code_at_${method}`);
        } catch (e) {
            // 本地存储失败不影响主流程
        }
    };

    const applySystemUserState = (method, isSystemUser) => {
        setSystemUserByMethod((prev) => ({
            ...prev,
            [method]: isSystemUser
        }));
        if (isSystemUser) {
            clearSendCodeCountdown(method);
        }
    };

    const scheduleSystemUserCheck = (method, account) => {
        const timerRef = method === 'phone' ? phoneSystemUserCheckTimerRef : emailSystemUserCheckTimerRef;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }

        const trimmedAccount = String(account ?? '').trim();
        if (!trimmedAccount) {
            applySystemUserState(method, false);
            return;
        }

        timerRef.current = setTimeout(async () => {
            const isSystemUser = await fetchIsSystemUser(trimmedAccount, method);
            applySystemUserState(method, isSystemUser);
        }, 400);
    };

    // 倒计时逻辑：支持刷新/重新打开弹窗后继续沿用上一轮60秒限制（手机号/邮箱分别计时）
    useEffect(() => {
        try {
            const methods = ['phone', 'email'];
            const nextCountdown = { phone: 0, email: 0 };

            methods.forEach((method) => {
                const lastSend = localStorage.getItem(`login_last_send_code_at_${method}`);
                if (!lastSend) return;
                const last = Number(lastSend);
                if (Number.isNaN(last)) return;
                const diffSec = Math.floor((Date.now() - last) / 1000);
                if (diffSec < 60) {
                    nextCountdown[method] = 60 - diffSec;
                }
            });

            setCountdownByMethod(nextCountdown);
        } catch (e) {
            // 读取失败直接忽略，不影响正常流程
        }
    }, []);

    useEffect(() => {
        const hasCountdown =
            (countdownByMethod.phone || 0) > 0 || (countdownByMethod.email || 0) > 0;
        if (!hasCountdown) {
            return undefined;
        }

        const timer = setTimeout(() => {
            setCountdownByMethod((prev) => ({
                phone: Math.max((prev.phone || 0) - 1, 0),
                email: Math.max((prev.email || 0) - 1, 0)
            }));
        }, 1000);

        return () => clearTimeout(timer);
    }, [countdownByMethod]);

    useEffect(() => {
        setVerificationCodeRequested(false);
    }, [loginMethod, phone, email]);

    useEffect(() => {
        scheduleSystemUserCheck('phone', phone);
        return () => {
            if (phoneSystemUserCheckTimerRef.current) {
                clearTimeout(phoneSystemUserCheckTimerRef.current);
            }
        };
    }, [phone]);

    useEffect(() => {
        scheduleSystemUserCheck('email', email);
        return () => {
            if (emailSystemUserCheckTimerRef.current) {
                clearTimeout(emailSystemUserCheckTimerRef.current);
            }
        };
    }, [email]);

    // 打开登录窗口时获取用户协议 & 隐私协议
    useEffect(() => {
        let cancelled = false;
        const fetchAgreements = async () => {
            setAgreementsLoading(true);
            try {
                const res = await post('/user/agreementsTemplate/validAgreement', {});
                const list = res?.data ?? [];
                if (!cancelled) {
                    setAgreements(Array.isArray(list) ? list : []);
                }
            } catch (err) {
                console.warn('Failed to get the agreement:', err);
            } finally {
                if (!cancelled) {
                    setAgreementsLoading(false);
                }
            }
        };
        fetchAgreements();
        return () => {
            cancelled = true;
        };
    }, []);

    // 发送验证码
    const handleSendCode = async () => {
        if (loginMethod === 'phone' && !phone) {
            toast.warning(t('login.warn.phoneRequired', 'Please enter the phone number'));
            return;
        }
        if (loginMethod === 'email' && !email) {
            toast.warning(t('login.warn.emailRequired', 'Please enter the email'));
            return;
        }
        
        if ((currentCountdown > 0 && !isCurrentSystemUser) || isSendCodeLoading) return;

        setIsSendCodeLoading(true);
        try {
            const account = loginMethod === 'phone' ? phone : email;
            const accountType = loginMethod === 'phone' ? '1' : '2';
            
            const data = await post('/user/common/sendCode', {
                account: account,
                accountType: accountType
            });
            
            console.log('验证码发送成功:', data);
            toast.success(t('login.success.codeSent', 'Verification code sent successfully'));
            setVerificationCodeRequested(true);
            const isSystemUser =
                systemUserByMethod[loginMethod] ||
                await fetchIsSystemUser(account, loginMethod);
            applySystemUserState(loginMethod, isSystemUser);
            if (!isSystemUser) {
                setCountdownByMethod((prev) => ({
                    ...prev,
                    [loginMethod]: 60
                }));
                try {
                    localStorage.setItem(`login_last_send_code_at_${loginMethod}`, String(Date.now()));
                } catch (e) {
                    // 本地存储失败不影响主流程
                }
            }
        } catch (error) {
            console.error('发送验证码错误:', error);
            toast.error(getRequestErrorMessage(error, {
                timeoutKey: 'login.error.sendCodeTimeout',
                timeoutFallback: 'Sending verification code timed out, please try again',
                networkKey: 'login.error.network',
                networkFallback: 'Network error, please check your connection and try again',
                requestExceptionKey: 'login.error.sendCodeException',
                requestExceptionFallback: 'Sending verification code failed due to a request error, please try again later'
            }));
        } finally {
            setIsSendCodeLoading(false);
        }
    };

    // 登录提交
    const finishLoginSession = (data) => {
        if (data?.data?.token) {
            localStorage.setItem('token', data.data.token);
            toast.success(t('login.success.login', 'Login successful'));
            onLoginSuccess?.();
            handleCloseCart();
            return true;
        }
        return false;
    };

    const promptRegisterConfirm = (restoreAccount) => {
        return new Promise((resolve) => {
            registerConfirmResolverRef.current = resolve;
            setRegisterConfirmState({ restoreAccount: Boolean(restoreAccount) });
        });
    };

    const closeRegisterConfirm = (confirmed) => {
        registerConfirmResolverRef.current?.(confirmed);
        registerConfirmResolverRef.current = null;
        setRegisterConfirmState(null);
    };

    const handleLogin = async () => {
        if (loginMethod === 'phone' && !phone) {
            toast.warning(t('login.warn.phoneRequired', 'Please enter the phone number'));
            return;
        }
        if (loginMethod === 'email' && !email) {
            toast.warning(t('login.warn.emailRequired', 'Please enter the email'));
            return;
        }
        if (!verificationCodeRequested) {
            toast.warning(t('login.warn.getCodeFirst', 'Please get the verification code'));
            return;
        }
        const code = verificationCode ? String(verificationCode).trim() : '';
        if (!code) {
            toast.warning(t('login.warn.codeRequired', 'Please enter the verification code'));
            return;
        }
        
        setIsLoginLoading(true);
        try {
            const account = loginMethod === 'phone' ? phone : email;
            const accountType = loginMethod === 'phone' ? '1' : '2';
            
            const buildPayload = (confirmRegister = false) => ({
                account,
                code,
                accountType,
                platform: 'web',
                confirmRegister,
            });

            let data = await post('/user/login', buildPayload(false));

            if (data.data?.needRegisterConfirm) {
                const confirmed = await promptRegisterConfirm(Boolean(data.data.restoreAccount));
                if (!confirmed) {
                    return;
                }
                data = await post('/user/login', buildPayload(true));
            }

            if (!finishLoginSession(data)) {
                throw new Error(t('login.error.login', 'Login failed, please try again later'));
            }
        } catch (error) {
            console.error('Login error:', error);
            toast.error(getRequestErrorMessage(error, {
                timeoutKey: 'login.error.loginTimeout',
                timeoutFallback: 'Login request timed out, please try again',
                networkKey: 'login.error.network',
                networkFallback: 'Network error, please check your connection and try again',
                requestExceptionKey: 'login.error.loginException',
                requestExceptionFallback: 'Login failed due to a request error, please try again later'
            }));
        } finally {
            setIsLoginLoading(false);
        }
    };

    // VK ID 授权登录（PKCE）
    const handleVKLogin = async () => {
        if (!VK_CONFIG.appId) {
            toast.warning(
              t(
                'login.warn.vkNotConfigured',
                'VK app is not configured. Please set REACT_APP_VK_APP_ID in .env'
              )
            );
            return;
        }
        try {
            const { state, codeChallenge } = await beginVkPkceSession();
            window.location.href = VK_CONFIG.buildAuthUrl({ state, codeChallenge });
        } catch (error) {
            console.error('VK login redirect error:', error);
            toast.error(t('vk.error.login', 'VK login failed'));
        }
    };

    // 从接口结果中挑选用户协议
    const pickUserAgreement = () => {
        if (!agreements.length) return null;
        const byCategory = agreements.find(a =>
            (a.category || '').toLowerCase().includes('user') ||
            (a.category || '').includes('用户')
        );
        if (byCategory) return byCategory;
        const byTitle = agreements.find(a =>
            (a.title || '').toLowerCase().includes('user') ||
            (a.title || '').includes('用户') ||
            (a.title || '').includes('服务')
        );
        if (byTitle) return byTitle;
        return agreements[0];
    };

    // 从接口结果中挑选隐私协议
    const pickPrivacyAgreement = () => {
        if (!agreements.length) return null;
        const byCategory = agreements.find(a =>
            (a.category || '').toLowerCase().includes('privacy') ||
            (a.category || '').includes('隐私')
        );
        if (byCategory) return byCategory;
        const byTitle = agreements.find(a =>
            (a.title || '').toLowerCase().includes('privacy') ||
            (a.title || '').includes('隐私')
        );
        if (byTitle) return byTitle;
        return agreements[1] || agreements[0];
    };

    const handleOpenAgreement = (type) => {
        if (agreementsLoading) {
            toast.info(t('login.info.loadingAgreement', 'Loading agreement content, please wait'));
            return;
        }
        const agreement = type === 'terms' ? pickUserAgreement() : pickPrivacyAgreement();
        if (!agreement) {
            toast.warning(t('login.warn.agreementMissing', 'No agreement content found'));
            return;
        }
        setActiveAgreement(agreement);
        setShowAgreementModal(true);
    };

    const registerConfirmDialog = registerConfirmState ? (
        <ConfirmDialog
            open
            title={
                registerConfirmState.restoreAccount
                    ? t('login.confirm.title.restore', 'Restore account')
                    : t('login.confirm.title.register', 'Create account')
            }
            message={
                registerConfirmState.restoreAccount
                    ? t('login.confirm.restore', 'This account was deactivated. Restore and continue?')
                    : t('login.confirm.register', 'No account found for this number/email. Create a new account?')
            }
            confirmLabel={t('login.confirm.yes', 'Confirm')}
            cancelLabel={t('login.confirm.cancel', 'Cancel')}
            onConfirm={() => closeRegisterConfirm(true)}
            onCancel={() => closeRegisterConfirm(false)}
        />
    ) : null;

    const loginContent = (
        <div className={styles.loginWindow}>
            <div className={styles.back} onClick={handleCloseCart} aria-hidden="true" />
            <AnimatedCart>
                <div className={styles.login}>
                    <div className={styles.top}>
                        <div className={styles.topHeader}>
                            <div>
                                <h2 className={styles.title}>{t('login.title', 'Welcome Back')}</h2>
                                <p className={styles.subtitle}>{t('login.subtitle', 'Continue Login')}</p>
                            </div>
                            <button 
                                className={styles.closeButton} 
                                onClick={handleCloseCart}
                                type="button"
                                aria-label={t("aria.close", "Close")}
                            >
                                <Cross className={styles.cross} />
                            </button>
                        </div>
                        
                        <div className={styles.loginForm}>
                            <div className={styles.methodTabs}>
                                <button 
                                    type="button"
                                    className={`${styles.methodTab} ${loginMethod === 'phone' ? styles.activeTab : ''}`}
                                    onClick={() => setLoginMethod('phone')}
                                >
                                    {t('login.tab.phone', 'Phone')}
                                </button>
                                <button 
                                    type="button"
                                    className={`${styles.methodTab} ${loginMethod === 'email' ? styles.activeTab : ''}`}
                                    onClick={() => setLoginMethod('email')}
                                >
                                    {t('login.tab.email', 'Email')}
                                </button>
                            </div>
                            
                            {loginMethod === 'phone' ? (
                                <div className={styles.formGroup}>
                                    <label htmlFor="phone-input">{t('login.label.phone', 'Phone')}</label>
                                    <div className={styles.phoneInputContainer}>
                                        <select 
                                            className={styles.countrySelect}
                                            value={selectedCountry}
                                            onChange={(e) => setSelectedCountry(e.target.value)}
                                            aria-label={t("login.aria.countryCode", "Country code")}
                                        >
                                            {countries.map(country => (
                                                <option key={country.code} value={country.code}>
                                                    {country.flag} {country.dialCode}
                                                </option>
                                            ))}
                                        </select>
                                        <input 
                                            id="phone-input"
                                            type="tel" 
                                            value={phone}
                                            onChange={(e) => setPhone(e.target.value)}
                                            placeholder={t('login.placeholder.phone', 'Enter your phone number')}
                                            autoComplete="tel"
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.formGroup}>
                                    <label htmlFor="email-input">{t('login.label.email', 'Email')}</label>
                                    <input 
                                        id="email-input"
                                        type="email" 
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('login.placeholder.email', 'Enter your email')}
                                        autoComplete="email"
                                    />
                                </div>
                            )}
                            
                            <div className={styles.codeInputContainer}>
                                <input 
                                    type="text" 
                                    inputMode="numeric"
                                    maxLength={8}
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value)}
                                    placeholder={t('login.placeholder.captcha', 'Enter Captcha')}
                                    aria-label={t("login.aria.verificationCode", "Verification code")}
                                />
                                <button 
                                    type="button"
                                    className={styles.sendCodeButton}
                                    onClick={handleSendCode}
                                    disabled={(currentCountdown > 0 && !isCurrentSystemUser) || isSendCodeLoading}
                                >
                                    {isSendCodeLoading ? (
                                        <span className={styles.loadingText}>{t('login.button.sending', 'Sending...')}</span>
                                    ) : currentCountdown > 0 && !isCurrentSystemUser ? (
                                        `${currentCountdown}s`
                                    ) : (
                                        t('login.button.getCaptcha', 'Get Captcha')
                                    )}
                                </button>
                            </div>
                            
                            <div className={styles.checkboxLabel}>
                                <span>
                                    {t('login.agreement.prefix', 'By logging in, you agree to our ')}
                                    <button
                                        type="button"
                                        className={styles.link}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleOpenAgreement('terms');
                                        }}
                                    >
                                        {t('login.agreement.user', 'User Agreement')}
                                    </button>
                                    {t('login.agreement.and', ' and ')}
                                    <button
                                        type="button"
                                        className={styles.link}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleOpenAgreement('privacy');
                                        }}
                                    >
                                        {t('login.agreement.privacy', 'Privacy Agreement')}
                                    </button>
                                </span>
                            </div>
                            
                            <button 
                                type="button"
                                className={styles.loginButton} 
                                onClick={handleLogin}
                                disabled={isLoginLoading}
                            >
                                {isLoginLoading ? (
                                    <span className={styles.loadingText}>{t('login.button.signingIn', 'Signing in...')}</span>
                                ) : (
                                    t('login.button.login', 'Login')
                                )}
                            </button>
                            
                            <div className={styles.thirdPartyLogin}>
                                <div className={styles.divider}>
                                    <span>{t('login.divider.orContinue', 'Or continue with')}</span>
                                </div>
                                <div className={styles.thirdPartyButtons}>
                                    <button 
                                        type="button"
                                        className={`${styles.thirdPartyButton} ${styles.vkButton}`}
                                        onClick={handleVKLogin}
                                    >
                                        <span className={styles.vkIcon}>VK</span>
                                        {t('login.button.vk', 'Continue with VK')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* 协议富文本只读弹窗 */}
                    {showAgreementModal && activeAgreement && (
                        <div className={styles.agreementOverlay}>
                            <div className={styles.agreementDialog}>
                                <div className={styles.agreementHeader}>
                                    <h3 className={styles.agreementTitle}>
                                        {activeAgreement.title || 'Agreement'}
                                    </h3>
                                    <button
                                        type="button"
                                        className={styles.closeButton}
                                        onClick={() => setShowAgreementModal(false)}
                                        aria-label={t("login.aria.closeAgreement", "Close agreement")}
                                    >
                                        <Cross className={styles.cross} />
                                    </button>
                                </div>
                                <div
                                    className={styles.agreementContent}
                                    dangerouslySetInnerHTML={{ __html: activeAgreement.content || '' }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </AnimatedCart>
        </div>
    );

    return (
        <>
            {ReactDOM.createPortal(loginContent, document.body)}
            {registerConfirmDialog}
        </>
    );
  }
  
  export default Login;